import React, { useEffect, useState } from 'react';
import { AnimatePresence } from 'framer-motion';
import { ToastCard } from './ToastCard';
import { toastStore } from './toast-store';
import { ToastItem, ToastPosition } from './toast-types';

export interface ToastContainerProps {
  position?: ToastPosition;
  className?: string;
  limit?: number;
  autoClose?: number | false;
  hideProgressBar?: boolean;
  newestOnTop?: boolean;
  closeOnClick?: boolean;
  rtl?: boolean;
  pauseOnFocusLoss?: boolean;
  draggable?: boolean;
  pauseOnHover?: boolean;
  theme?: 'light' | 'dark' | 'colored';
  [key: string]: any;
}

const positionClasses: Record<ToastPosition, string> = {
  'top-left': 'top-5 left-5 items-start',
  'top-center': 'top-5 left-1/2 -translate-x-1/2 items-center',
  'top-right': 'top-5 right-5 items-end',
  'bottom-left': 'bottom-5 left-5 items-start',
  'bottom-center': 'bottom-6 left-1/2 -translate-x-1/2 items-center',
  'bottom-right': 'bottom-6 right-6 items-end',
};

export const ToastContainer: React.FC<ToastContainerProps> = ({
  position = 'top-right',
  className = '',
  limit,
}) => {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  useEffect(() => {
    const unsubscribe = toastStore.subscribe((newToasts) => {
      setToasts(newToasts);
    });
    return () => unsubscribe();
  }, []);

  const handleDismiss = (id: string | number) => {
    toastStore.dismiss(id);
  };

  // Group toasts by position
  const positions: ToastPosition[] = [
    'top-left',
    'top-center',
    'top-right',
    'bottom-left',
    'bottom-center',
    'bottom-right',
  ];

  return (
    <div className="fixed inset-0 pointer-events-none z-[99999] overflow-hidden">
      {positions.map((pos) => {
        const activeToastsForPos = toasts.filter(
          (t) => (t.position || position) === pos,
        );

        if (activeToastsForPos.length === 0) return null;

        const visibleToasts = limit
          ? activeToastsForPos.slice(-limit)
          : activeToastsForPos;

        return (
          <div
            key={pos}
            className={`fixed flex flex-col gap-2.5 max-w-full p-4 pointer-events-none ${
              positionClasses[pos]
            } ${className}`}
          >
            <AnimatePresence initial={false}>
              {visibleToasts.map((toastItem) => (
                <ToastCard
                  key={toastItem.id}
                  toast={toastItem}
                  onDismiss={handleDismiss}
                />
              ))}
            </AnimatePresence>
          </div>
        );
      })}
    </div>
  );
};

// Aliases
export const Toaster = ToastContainer;
export const NotificationContainer = ToastContainer;
